<template>
    <div class="">
        <!-- DataTable Component -->
        <DataTable
            :key="tableKey"
            :columns="tableColumns"
            :items="paginatedItems"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :selectedItems="selectedItems"
            :enableMultiSelect="true"
            :enableDragDrop="false"
            @page-changed="handlePageChange"
            :perPage="itemsPerPage"
            @per-page-changed="handlePerPageChange"
            @item-selected="toggleItemSelection"
            @select-all-toggled="toggleSelectAll"
            @sort="handleSort"
        >
            <template #column-actions="{ item }">
                <div class="ppv2-relative ppv2-flex ppv2-items-center ppv2-justify-center">
                    <button
                        @click.stop="toggleActionMenu(item.id)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :ref="`actionButton-${item.id}`"
                    >
                        <MoreVertical :size="18" />
                    </button>

                    <!-- Actions Dropdown Menu -->
                    <div
                        v-if="activeActionMenu === item.id"
                        class="actionsDropdown ppv2-fixed ppv2-rounded-xl ppv2-overflow-hidden ppv2-z-[100] ppv2-min-w-[220px] ppv2-shadow-lg"
                        :style="dropdownPosition"
                        :ref="`actionMenu-${item.id}`"
                    >
                        <!-- Modifier -->
                        <a
                            :href="`/back-office/v3/roles/${item.id}/edit`"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <FilePen :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('common.edit')
                            }}</span>
                        </a>

                        <!-- Supprimer -->
                        <div
                            @click="deleteSingleItem(item.id)"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <Trash2 :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('common.delete')
                            }}</span>
                        </div>
                    </div>
                </div>
            </template>
            <!-- Toolbar Slot -->
            <template #toolbar>
                <div class="ppv2-flex md:ppv2-flex-row ppv2-flex-col ppv2-gap-2 ppv2-justify-between ppv2-items-center ppv2-mb-6">
                    <div class="ppv2-flex ppv2-flex-wrap ppv2-gap-3">
                        <!-- Search Input -->
                        <div class="ppv2-relative">
                            <Search v-model="searchQuery" :emitGlobal="false" @search-updated="handleSearchUpdate" />
                        </div>
                    </div>

                    <!-- Create Button -->
                    <BaseButton
                        variant="purple"
                        :custom-height="40"
                        :custom-font-size="14"
                        custom-class="ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px]"
                        @click="navigateToCreate"
                    >
                        <Plus :size="17" />
                        <span class="poppinsRegular">{{ $t('common.create') }}</span>
                    </BaseButton>
                </div>
            </template>

            <!-- Bulk Actions Slot -->
            <template #bulk-actions="{ selectedCount }">
                <span :style="{ fontSize: normalizeFontSize(15) }" class="poppinsRegular textGray">
                    {{ $t('roles.selected_count', { count: selectedCount, plural: selectedCount > 1 ? 's' : '' }) }}
                </span>
                <span class="poppinsRegular ppv2-text-white ppv2-text-opacity-50" :style="{ fontSize: normalizeFontSize(15) }"
                    >|</span
                >
                <BaseButton
                    variant="outline"
                    @click="deleteSelectedItems"
                    customClass="!ppv2-px-4 !ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                >
                    <Trash2 :size="14" />
                    <span>{{ $t('common.deleteSelection') }}</span>
                </BaseButton>
            </template>
        </DataTable>

        <!-- Delete Confirmation Popup -->
        <CustomDialog v-model="showDeletePopup" :defaultTitle="deletePopupTitle" customClassTitle="poppinsSemiBold" noScroll>
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ deletePopupDescription }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    id="button"
                    type="button"
                    variant="outline"
                    @click="showDeletePopup = false"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('common.cancel') }}
                </BaseButton>
                <BaseButton
                    type="submit"
                    variant="danger"
                    @click="confirmDelete"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('common.confirm') }}
                </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import { Plus, Trash2, MoreVertical, FilePen } from 'lucide-vue'
import normalizeSize from '../../../utils/normalizeSize'
import Search from '../Experience/components/Search.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import DataTable from '../../Shared/DataTable.vue'
import axios from 'axios'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../../utils/perPage'

export default {
    name: 'RolesTable',
    props: {
        token: {
            type: String,
            required: true,
        },
    },
    components: {
        Plus,
        Trash2,
        Search,
        BaseButton,
        CustomDialog,
        DataTable,
        MoreVertical,
        FilePen,
    },
    data() {
        return {
            searchQuery: '',
            currentPage: 1,
            itemsPerPage: getPerPage('roles_items_per_page', 10),
            selectedItems: [],
            selectAll: false,
            isLoading: false,
            error: null,
            tableColumns: [
                { key: 'name', label: this.$t('common.name'), sortable: true, type: 'text' },
                { key: 'display_name', label: this.$t('roles.display_name'), sortable: true, type: 'text' },
                { key: 'actions', label: this.$t('calendar.modal.actions'), sortable: false, type: 'actions' },
            ],
            showDeletePopup: false,
            deletePopupTitle: this.$t('roles.delete_title'),
            deletePopupDescription: '',
            deleteAction: null,
            itemToDelete: null,
            items: {
                data: [],
                total: 0,
                last_page: 1,
            },
            tableKey: 0,
            orderBy: 'id',
            orderDirection: 'desc',
            activeActionMenu: null,
            dropdownPosition: { top: '0px', left: '0px' },
        }
    },
    computed: {
        paginatedItems() {
            return this.items.data || []
        },
        totalPages() {
            return this.items.last_page || 1
        },
    },
    mounted() {
        this.fetchItems()
        document.addEventListener('click', this.closeActionMenu)
        window.addEventListener('scroll', this.updateDropdownPosition, true)
        window.addEventListener('resize', this.updateDropdownPosition)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.closeActionMenu)
        window.removeEventListener('scroll', this.updateDropdownPosition, true)
        window.removeEventListener('resize', this.updateDropdownPosition)
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        navigateToCreate() {
            window.location.href = '/back-office/v3/roles/create'
        },
        handlePageChange(page) {
            this.currentPage = page
            this.fetchItems()
        },
        handlePerPageChange(perPage) {
            this.itemsPerPage = Number(perPage)
            setPerPage('roles_items_per_page', this.itemsPerPage)
            this.currentPage = 1
            this.fetchItems()
        },
        handleSearchUpdate(searchValue) {
            this.searchQuery = searchValue
            this.currentPage = 1
            this.fetchItems()
        },
        handleSort(key) {
            if (this.orderBy === key) {
                this.orderDirection = this.orderDirection === 'asc' ? 'desc' : 'asc'
            } else {
                this.orderBy = key
                this.orderDirection = 'asc'
            }
            this.currentPage = 1
            this.fetchItems()
        },
        toggleItemSelection(itemId) {
            const index = this.selectedItems.indexOf(itemId)
            if (index > -1) {
                this.selectedItems.splice(index, 1)
            } else {
                this.selectedItems.push(itemId)
            }
        },
        toggleSelectAll() {
            const allIds = this.paginatedItems.map((item) => item.id)
            const allSelected = allIds.length > 0 && allIds.every((id) => this.selectedItems.includes(id))
            if (allSelected) {
                this.selectedItems = []
            } else {
                this.selectedItems = [...allIds]
            }
        },
        async deleteSelectedItems() {
            if (this.selectedItems.length === 0) return
            this.deleteAction = 'multiple'
            this.deletePopupDescription = this.$t('roles.delete_confirm_multiple')
            this.showDeletePopup = true
        },
        deleteSingleItem(itemId) {
            this.deleteAction = 'single'
            this.itemToDelete = itemId
            this.deletePopupDescription = this.$t('roles.delete_confirm_single')
            this.showDeletePopup = true
            this.activeActionMenu = null
        },
        async confirmDelete() {
            try {
                let ids = []
                if (this.deleteAction === 'single') {
                    ids = [this.itemToDelete]
                } else {
                    ids = this.selectedItems
                }

                const response = await axios.post(
                    `/api/v3/roles/delete`,
                    { ids: ids },
                    {
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                        },
                    }
                )

                if (response.data.success) {
                    this.fetchItems()
                    this.selectedItems = []
                    this.selectAll = false
                } else {
                    alert(response.data.message || this.$t('common.error_occurred'))
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
            this.showDeletePopup = false
            this.deleteAction = null
            this.itemToDelete = null
        },
        async fetchItems() {
            this.isLoading = true
            this.error = null
            try {
                let url = `/api/v3/roles?currentPage=${this.currentPage}&itemsPerPage=${this.itemsPerPage}&search=${this.searchQuery}&orderBy=${this.orderBy}&orderDirection=${this.orderDirection}`
                const response = await axios.get(url, {
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                    },
                })
                const data = response.data
                this.items = {
                    data: data.data || [],
                    total: data.total || 0,
                    last_page: data.last_page || 1,
                }
            } catch (err) {
                this.error = err.message || this.$t('roles.errors.load_error')
                console.error('API error:', err)
            } finally {
                this.isLoading = false
            }
        },
        toggleActionMenu(itemId) {
            if (this.activeActionMenu === itemId) {
                this.activeActionMenu = null
            } else {
                this.activeActionMenu = itemId
                this.$nextTick(() => {
                    this.calculateDropdownPosition(itemId)
                })
            }
        },
        calculateDropdownPosition(itemId) {
            const button = this.$refs[`actionButton-${itemId}`]
            const btnEl = Array.isArray(button) ? button[0] : button
            if (!btnEl) return

            const rect = btnEl.getBoundingClientRect()
            this.dropdownPosition = {
                top: `${rect.bottom + 4}px`,
                left: `${rect.right - 220}px`,
            }
        },
        updateDropdownPosition() {
            if (this.activeActionMenu) {
                this.calculateDropdownPosition(this.activeActionMenu)
            }
        },
        closeActionMenu(event) {
            if (this.activeActionMenu) {
                const button = this.$refs[`actionButton-${this.activeActionMenu}`]
                const menu = this.$refs[`actionMenu-${this.activeActionMenu}`]
                const btnEl = Array.isArray(button) ? button[0] : button
                const menuEl = Array.isArray(menu) ? menu[0] : menu

                if (btnEl && !btnEl.contains(event.target) && menuEl && !menuEl.contains(event.target)) {
                    this.activeActionMenu = null
                }
            }
        },
    },
    watch: {
        selectedItems: {
            handler(newSelection) {
                const currentPageIds = this.paginatedItems.map((item) => item.id)
                this.selectAll = currentPageIds.length > 0 && currentPageIds.every((id) => newSelection.includes(id))
            },
            deep: true,
        },
    },
}
</script>

<style scoped lang="scss">
.textGray {
    color: #1a1f37;
}

.actionsDropdown {
    position: fixed;
    background: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    padding: 4px 0;
}

.actionItem {
    background: transparent;
    border: none;
    color: #1a1f37;
    text-decoration: none;
    display: flex;
    align-items: center;
}

.actionItem:hover {
    background-color: #f3f4f6;
    color: #1a1f37 !important;
}

.dark {
    .actionsDropdown {
        background-color: #2c2c2ce5;
    }

    .actionItem {
        color: #bfbfbf;
    }

    .actionItem:hover {
        background-color: #1a1f37;
        color: white !important;
    }
}
</style>
